import { addTokenHeader, BusinessAxiosConfig } from '@src/config/AxiosConfig';
import { BUSINESS_API_VERSION } from '@src/constants';
import ClientApi from '@src/types/client';
import { getState } from '@src/utils';
import { AxiosResponse } from 'axios';
import { getApiEndpointResponse } from './api-service';
import { setFormIfValueIsDefined } from '@src/utils/setFormIfValueIsDefined';

const setBusinessInformationInFormData = (
  businessInformationEntries: [
    string,
    (
      | string
      | File
      | Partial<{
          Address: string;
          City: string;
          ZipCode: string;
          StateId: string;
          RegisteredAddressEvidence: File;
        }>[]
    )
  ][],
  formData: FormData
) => {
  businessInformationEntries.forEach(([key, value]) => {
    if (key === 'BusinessAddress') {
      const businessAddresses = (value ||
        []) as ClientApi.Onboarding.SubmitBusinessDetails.BusinessAddress[];
      businessAddresses.forEach((address, index) => {
        const addressEntries = Object.entries(address);
        addressEntries.forEach(([addressKey, addressValue]) => {
          setFormIfValueIsDefined(
            formData,
            `BusinessInformation.BusinessAddress${index}.${addressKey}`,
            addressValue
          );
        });
      });
    } else {
      setFormIfValueIsDefined(formData, `BusinessInformation.${key}`, value);
    }
  });
};

const setBusinessDocumentsInFormData = (
  businessDocumentsEntries: [
    string,
    (
      | string
      | File
      | Partial<{
          Title: string;
          AmlDocumentation: File;
        }>[]
    )
  ][],
  formData: FormData
) => {
  businessDocumentsEntries.forEach(([key, value]) => {
    if (key === 'OtherComplianceDocs') {
      const otherComplianceDocs = (value ||
        []) as ClientApi.Onboarding.SubmitBusinessDetails.BusinessAddress[];
      otherComplianceDocs.forEach((doc, index) => {
        const docEntries = Object.entries(doc);
        docEntries.forEach(([docKey, docValue]) => {
          setFormIfValueIsDefined(
            formData,
            `BusinessDocuments.OtherComplianceDocument${index}.${docKey}`,
            docValue
          );
        });
      });
    } else {
      setFormIfValueIsDefined(formData, `BusinessDocuments.${key}`, value);
    }
  });
};

export const submitBusinessDetailsService = async (
  payload: ClientApi.Onboarding.SubmitBusinessDetails.Request
): Promise<boolean> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');
  const { businessDocuments, businessInformation, controlPersonInformation } = payload;
  const formData = new FormData();

  const businessInformationEntries = Object.entries(businessInformation);
  setBusinessInformationInFormData(businessInformationEntries, formData);

  const businessDocumentsEntries = Object.entries(businessDocuments);
  setBusinessDocumentsInFormData(businessDocumentsEntries, formData);

  controlPersonInformation.forEach((person, index) => {
    setFormIfValueIsDefined(
      formData,
      `ControlPersonsInformation${index}.FirstName`,
      person.FirstName
    );
    setFormIfValueIsDefined(
      formData,
      `ControlPersonsInformation${index}.LastName`,
      person.LastName
    );
    setFormIfValueIsDefined(
      formData,
      `ControlPersonsInformation${index}.DateOfBirth`,
      person.DateOfBirth
    );
    setFormIfValueIsDefined(formData, `ControlPersonsInformation${index}.IdType`, person.IdType);
    setFormIfValueIsDefined(
      formData,
      `ControlPersonsInformation${index}.GovernmentId`,
      person.GovernmentId
    );
    setFormIfValueIsDefined(formData, `ControlPersonsInformation${index}.Address`, person.Address);
    setFormIfValueIsDefined(formData, `ControlPersonsInformation${index}.City`, person.City);
    setFormIfValueIsDefined(formData, `ControlPersonsInformation${index}.StateId`, person.StateId);
  });

  return BusinessAxiosConfig.post<
    ClientApi.Onboarding.SubmitBusinessDetails.Response,
    AxiosResponse<ClientApi.Onboarding.SubmitBusinessDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Onboarding/SubmitBusinessDetails`, formData, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const UpdateBusinessLocationService = async (
  payload: ClientApi.Onboarding.UpdateBusinessLocation.Request
): Promise<ClientApi.Onboarding.UpdateBusinessLocation.Response['data']> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;
  const config = addTokenHeader(token || '');

  return BusinessAxiosConfig.put<
    ClientApi.Onboarding.UpdateBusinessLocation.Response,
    AxiosResponse<ClientApi.Onboarding.UpdateBusinessLocation.Response>
  >(`/api/v${BUSINESS_API_VERSION}/Onboarding/UpdateBusinessLocation`, payload, config).then(
    (res) => res.data.data
  );
};

export const submitBusinessInformationService = async (
  payload: ClientApi.Onboarding.SubmitBusinessInformation.Request
): Promise<boolean> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');
  const formData = new FormData();

  setFormIfValueIsDefined(formData, 'LogoFile', payload.LogoFile);
  setFormIfValueIsDefined(formData, 'BusinessTypeId', payload.BusinessTypeId);
  setFormIfValueIsDefined(formData, 'BusinessIndustryId', payload.BusinessIndustryId);
  setFormIfValueIsDefined(formData, 'Name', payload.Name);
  setFormIfValueIsDefined(formData, 'PhoneNumber', payload.PhoneNumber);
  setFormIfValueIsDefined(formData, 'EmailAddress', payload.EmailAddress);
  setFormIfValueIsDefined(formData, 'About', payload.About);
  setFormIfValueIsDefined(formData, 'IsEdit', payload.IsEdit);

  if (payload.Address) {
    payload.Address.forEach((address, index) => {
      setFormIfValueIsDefined(formData, `Address[${index}].Address`, address.Address);
      setFormIfValueIsDefined(formData, `Address[${index}].City`, address.City);
      setFormIfValueIsDefined(formData, `Address[${index}].StateId`, address.StateId);
      setFormIfValueIsDefined(
        formData,
        `Address[${index}].RegisteredAddressEvidence`,
        address.RegisteredAddressEvidence
      );
      setFormIfValueIsDefined(formData, `Address[${index}].ZipCode`, address.ZipCode);
    });
  }

  if (payload.TradeName) {
    setFormIfValueIsDefined(formData, 'TradeName', payload.TradeName);
  }
  if (payload.WebsiteUrl) {
    setFormIfValueIsDefined(formData, 'WebsiteUrl', payload.WebsiteUrl);
  }
  if (payload.BusinessType) {
    setFormIfValueIsDefined(formData, 'BusinessType', payload.BusinessType);
  }
  if (payload.CompanyRegistrationNumber) {
    setFormIfValueIsDefined(
      formData,
      'CompanyRegistrationNumber',
      payload.CompanyRegistrationNumber
    );
  }
  if (payload.TaxIdentificationNumber) {
    setFormIfValueIsDefined(formData, 'TaxIdentificationNumber', payload.TaxIdentificationNumber);
  }
  if (payload.EinNumber) {
    setFormIfValueIsDefined(formData, 'EinNumber', payload.EinNumber);
  }
  if (payload.EstimatedTransactionValueInUsd) {
    setFormIfValueIsDefined(
      formData,
      'EstimatedTransactionValueInUsd',
      payload.EstimatedTransactionValueInUsd
    );
  }
  if (payload.PciDssLevel) {
    setFormIfValueIsDefined(formData, 'PciDssLevel', payload.PciDssLevel);
  }
  if (payload.FraudToolInPlace) {
    setFormIfValueIsDefined(formData, 'FraudToolInPlace', payload.FraudToolInPlace);
  }

  return BusinessAxiosConfig.post<
    ClientApi.Onboarding.SubmitBusinessInformation.Response,
    AxiosResponse<ClientApi.Onboarding.SubmitBusinessInformation.Response>
  >(`/api/v${BUSINESS_API_VERSION}/BusinessInformation/Create`, formData, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const submitBusinessDocumentsService = async (
  payload: ClientApi.Onboarding.SubmitBusinessDocuments.Request
): Promise<boolean> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');
  const formData = new FormData();

  setFormIfValueIsDefined(formData, 'IsEdit', payload.IsEdit);
  if (payload.CertificateOfIncorporation) {
    setFormIfValueIsDefined(
      formData,
      'CertificateOfIncorporation',
      payload.CertificateOfIncorporation
    );
  }

  if (payload.CompanyRegistrationNumber) {
    setFormIfValueIsDefined(
      formData,
      'CompanyRegistrationNumber',
      payload.CompanyRegistrationNumber
    );
  }
  if (payload.TaxIdentificationNumber) {
    setFormIfValueIsDefined(formData, 'TaxIdentificationNumber', payload.TaxIdentificationNumber);
  }

  if (payload.BusinessType) {
    setFormIfValueIsDefined(formData, 'BusinessType', payload.BusinessType);
  }

  if (payload.ShareHolderDocument) {
    setFormIfValueIsDefined(formData, 'ShareHolderDocument', payload.ShareHolderDocument);
  }

  if (payload.OtherComplianceDocuments) {
    payload.OtherComplianceDocuments.forEach((doc, index) => {
      setFormIfValueIsDefined(formData, `OtherComplianceDocuments[${index}].Id`, doc.Id);
      setFormIfValueIsDefined(formData, `OtherComplianceDocuments[${index}].Title`, doc.Title);
      setFormIfValueIsDefined(
        formData,
        `OtherComplianceDocuments[${index}].OtherDocumentationType`,
        doc.OtherDocumentationType
      );
    });
  }

  if (payload.BusinessType) {
    setFormIfValueIsDefined(formData, 'BusinessType', payload.BusinessType);
  }

  if (payload.AgentOnboardingKYBForm) {
    setFormIfValueIsDefined(formData, 'AgentOnboardingKYBForm', payload.AgentOnboardingKYBForm);
  }
  if (payload.AmlDocumentation) {
    setFormIfValueIsDefined(formData, 'AmlDocumentation', payload.AmlDocumentation);
  }
  if (payload.EinCertificate) {
    setFormIfValueIsDefined(formData, 'EinCertificate', payload.EinCertificate);
  }
  if (payload.IrsCertificate) {
    setFormIfValueIsDefined(formData, 'IrsCertificate', payload.IrsCertificate);
  }
  if (payload.CertificateOfGoodStanding) {
    setFormIfValueIsDefined(
      formData,
      'CertificateOfGoodStanding',
      payload.CertificateOfGoodStanding
    );
  }
  if (payload.BusinessDBA) {
    setFormIfValueIsDefined(formData, 'BusinessDBA', payload.BusinessDBA);
  }
  if (payload.KybForm) {
    setFormIfValueIsDefined(formData, 'KybForm', payload.KybForm);
  }
  if (payload.TaxDocument) {
    setFormIfValueIsDefined(formData, 'TaxDocument', payload.TaxDocument);
  }
  if (payload.ProofOfAddress) {
    setFormIfValueIsDefined(formData, 'ProofOfAddress', payload.ProofOfAddress);
  }
  if (payload.BoardResolution) {
    setFormIfValueIsDefined(formData, 'BoardResolution', payload.BoardResolution);
  }
  if (payload.PartnershipAgreement) {
    setFormIfValueIsDefined(formData, 'PartnershipAgreement', payload.PartnershipAgreement);
  }
  if (payload.CR12) {
    setFormIfValueIsDefined(formData, 'CR12', payload.CR12);
  }
  if (payload.Memart) {
    setFormIfValueIsDefined(formData, 'Memart', payload.Memart);
  }

  return BusinessAxiosConfig.post<
    ClientApi.Onboarding.SubmitBusinessDocuments.Response,
    AxiosResponse<ClientApi.Onboarding.SubmitBusinessDocuments.Response>
  >(`/api/v${BUSINESS_API_VERSION}/BusinessInformation/Documents`, formData, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const submitBusinessAddressService = async (
  payload: ClientApi.Onboarding.SubmitBusinessAddress.Request
): Promise<boolean> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');
  const formData = new FormData();

  const businessType = payload?.requests?.[0]?.businessType;

  setFormIfValueIsDefined(formData, `IsEdit`, payload.IsEdit);

  payload.requests.forEach((request, index) => {
    setFormIfValueIsDefined(formData, `requests[${index}].id`, request.id);
    setFormIfValueIsDefined(formData, `requests[${index}].homeAddress`, request.homeAddress);
    setFormIfValueIsDefined(formData, `requests[${index}].city`, request.city);
    setFormIfValueIsDefined(formData, `requests[${index}].zipCode`, request.zipCode);
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].stateOfResidence`,
      request.stateOfResidence
    );
    setFormIfValueIsDefined(formData, `requests[${index}].firstName`, request.firstName);
    setFormIfValueIsDefined(formData, `requests[${index}].lastName`, request.lastName);
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].countryOfResidence`,
      request.countryOfResidence
    );
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].uboBusinessDocuments.idType`,
      request.uboBusinessDocuments?.idType
    );
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].uboBusinessDocuments.idNumber`,
      request.uboBusinessDocuments?.idNumber
    );
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].uboBusinessDocuments.uboIdentificationDocument`,
      request.uboBusinessDocuments?.uboIdentificationDocument
    );
    setFormIfValueIsDefined(
      formData,
      `requests[${index}].homePhoneNumber`,
      request.homePhoneNumber
    );
    setFormIfValueIsDefined(formData, `requests[${index}].email`, request.email);
    if (request.uboBusinessDocuments?.uboIdentificationDocumentBackSide) {
      setFormIfValueIsDefined(
        formData,
        `requests[${index}].uboBusinessDocuments.uboIdentificationDocumentBackSide`,
        request.uboBusinessDocuments?.uboIdentificationDocumentBackSide
      );
    }
    if (request.dateOfBirth) {
      setFormIfValueIsDefined(formData, `requests[${index}].dateOfBirth`, request.dateOfBirth);
    }
    if (request.role) {
      setFormIfValueIsDefined(formData, `requests[${index}].role`, request.role);
    }
    if (request.shareSize) {
      setFormIfValueIsDefined(formData, `requests[${index}].shareSize`, request.shareSize);
    }
    if (request.gender) {
      setFormIfValueIsDefined(formData, `requests[${index}].gender`, request.gender);
    }
    if (request.socialSecurityNumber) {
      setFormIfValueIsDefined(
        formData,
        `requests[${index}].socialSecurityNumber`,
        request.socialSecurityNumber
      );
    }
    if (request.businessType) {
      setFormIfValueIsDefined(formData, `requests[${index}].businessType`, request.businessType);
    }
    if (request.citizenship) {
      setFormIfValueIsDefined(formData, `requests[${index}].citizenship`, request.citizenship);
    }
    if (request.occupation) {
      setFormIfValueIsDefined(formData, `requests[${index}].occupation`, request.occupation);
    }
  });

  return BusinessAxiosConfig.post<
    ClientApi.Onboarding.SubmitBusinessAddress.Response,
    AxiosResponse<ClientApi.Onboarding.SubmitBusinessAddress.Response>
  >(
    `/api/v${BUSINESS_API_VERSION}/BusinessInformation/BeneficiaryOwner?BusinessType=${businessType}`,
    formData,
    config
  )
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getBusinessInformationCompletionCheckService = async (): Promise<
  ClientApi.Onboarding.GetBusinessInformationCompletionCheck.Response['data']
> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');

  return BusinessAxiosConfig.get<
    ClientApi.Onboarding.GetBusinessInformationCompletionCheck.Response,
    AxiosResponse<ClientApi.Onboarding.GetBusinessInformationCompletionCheck.Response>
  >(`/api/v${BUSINESS_API_VERSION}/BusinessInformation/BusinessInformationCompletionChecks`, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};

export const getBusinessInformationDetailsService = async (): Promise<
  ClientApi.Onboarding.GetBusinessInformationDetails.Response['data']
> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'formData');

  return BusinessAxiosConfig.get<
    ClientApi.Onboarding.GetBusinessInformationDetails.Response,
    AxiosResponse<ClientApi.Onboarding.GetBusinessInformationDetails.Response>
  >(`/api/v${BUSINESS_API_VERSION}/BusinessInformation/BusinessInformationDetails`, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};
export const submitBusinessActivationService = async (): Promise<boolean> => {
  const state = getState();
  const token = state?.tokens?.token?.accessToken;

  const config = addTokenHeader(token || '', 'json');

  return BusinessAxiosConfig.post<
    ClientApi.Onboarding.SubmitBusinessActivation.Response,
    AxiosResponse<ClientApi.Onboarding.SubmitBusinessActivation.Response>
  >(`/api/v${BUSINESS_API_VERSION}/BusinessInformation/ActivateBusiness`, {}, config)
    .then(getApiEndpointResponse)
    .then((res) => res.data);
};
